import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, CheckCircle2, AlertCircle, Bookmark } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";

export default function InventoryKpiStrip() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [counts, setCounts] = useState({
    total: 0,
    assignable: 0,
    nonAssignable: 0,
    assigned: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchCounts = async () => {
    try {
      setLoading(true);
      const token = Cookies.get("token");
      const res = await axios.get("https://uptask.visiontrek.io/api/inventory", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        const items = res.data.data || [];
        const assignable = items.filter((item) => item.status === "Assignable").length;
        const nonAssignable = items.filter((item) => item.status === "Not Assignable").length;
        const assigned = items.filter((item) => item.status === "Assigned" || item.status === "In Use").length;

        setCounts({
          total: items.length,
          assignable,
          nonAssignable,
          assigned,
        });
      }
    } catch (err) {
      console.error("Error fetching inventory counts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();

    // Listen to custom update events
    window.addEventListener("inventory-updated", fetchCounts);
    return () => {
      window.removeEventListener("inventory-updated", fetchCounts);
    };
  }, []);

  const scrollToList = () => {
    setTimeout(() => {
      const el = document.getElementById("inventory-list-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  const handleCardClick = (statusVal) => {
    const targetPath = "/inventory/all";
    if (location.pathname === targetPath) {
      if (statusVal === "all") {
        searchParams.delete("status");
      } else {
        searchParams.set("status", statusVal);
      }
      setSearchParams(searchParams);
      scrollToList();
    } else {
      if (statusVal === "all") {
        navigate(targetPath);
      } else {
        navigate(`${targetPath}?status=${statusVal}`);
      }
      scrollToList();
    }
  };

  const kpis = [
    {
      label: "Total Inventory",
      value: counts.total,
      subtext: "Items registered",
      icon: Package,
      bg: "bg-[#E0F2FE]", // Light blue
      iconBg: "bg-blue-200/50",
      iconColor: "text-[#0EA5E9]",
      textColor: "text-blue-900",
      subtextColor: "text-blue-600/80",
      onClick: () => handleCardClick("all"),
      isActive: location.pathname === "/inventory/all" && !searchParams.get("status"),
    },
    {
      label: "Assignable",
      value: counts.assignable,
      subtext: "Ready to deploy",
      icon: CheckCircle2,
      bg: "bg-[#E0FBEA]", // Light green
      iconBg: "bg-green-200/50",
      iconColor: "text-[#22C55E]",
      textColor: "text-green-900",
      subtextColor: "text-green-600/80",
      onClick: () => handleCardClick("assignable"),
      isActive: location.pathname === "/inventory/all" && searchParams.get("status") === "assignable",
    },
    {
      label: "Non Assignable",
      value: counts.nonAssignable,
      subtext: "Restricted items",
      icon: AlertCircle,
      bg: "bg-[#FEE2E2]", // Light red
      iconBg: "bg-red-200/50",
      iconColor: "text-[#EF4444]",
      textColor: "text-red-900",
      subtextColor: "text-red-600/80",
      onClick: () => handleCardClick("not-assignable"),
      isActive: location.pathname === "/inventory/all" && searchParams.get("status") === "not-assignable",
    },
    {
      label: "Assigned Count",
      value: counts.assigned,
      subtext: "Active deployments",
      icon: Bookmark,
      bg: "bg-[#F3E8FF]", // Light purple
      iconBg: "bg-purple-200/50",
      iconColor: "text-[#9333EA]",
      textColor: "text-purple-900",
      subtextColor: "text-purple-600/80",
      onClick: () => handleCardClick("assigned"),
      isActive: location.pathname === "/inventory/all" && searchParams.get("status") === "assigned",
    },
  ];

  if (loading) {
    return (
      <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-white/10 rounded-2xl border border-white/10" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-5xl mx-auto w-full">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
        {kpis.map((kpi, idx) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              onClick={kpi.onClick}
              className={`
                relative overflow-hidden cursor-pointer
                p-5 rounded-2xl border transition-all duration-300
                flex flex-col justify-between h-32 select-none
                ${kpi.bg}
                ${kpi.isActive ? "ring-2 ring-purple-600 ring-offset-2 scale-[1.02] shadow-lg" : "border-white/10 shadow-md hover:scale-[1.02] hover:shadow-lg"}
              `}
            >
              {/* Card Header */}
              <div className="flex justify-between items-start w-full">
                <span className={`text-[10px] font-black uppercase tracking-wider ${kpi.textColor}`}>
                  {kpi.label}
                </span>
                <div className={`p-2 rounded-xl ${kpi.iconBg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${kpi.iconColor}`} />
                </div>
              </div>

              {/* Card Footer */}
              <div className="mt-auto">
                <h3 className={`text-2xl font-black leading-none mb-1 ${kpi.textColor}`}>
                  {kpi.value}
                </h3>
                <p className={`text-[10px] font-bold tracking-tight ${kpi.subtextColor}`}>
                  {kpi.subtext}
                </p>
              </div>

              {/* Active Badge */}
              {kpi.isActive && (
                <div className="absolute top-2 right-12 bg-purple-600 text-[8px] font-black uppercase tracking-widest text-white px-2 py-0.5 rounded-full scale-75 animate-bounce">
                  Active
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
